import { useMemo } from "react";
import dayjs from "dayjs";
import { getPercentKey, OTHER } from "@/pages/query/common";
import { DbtHealthCheckResponse, GetSnowflakeJobGraphData } from "@/pages/query/types";
import { ChartTypeEnum } from "./DbtJobsGraph";

export const COVERAGE_COLORS = [
  "#247EFE",
  "#FF6B6B",
  "#4ECDC4",
  "#FFD93D",
  "#6C5CE7",
  "#A29BFE",
  "#FD79A8",
  "#00B894",
];

/**
 * Build a human-readable display label for a dbt workflow entry.
 * Uses integration_name/environment_name when available, falls back to
 * extracting the integration ID from the workflow_id key.
 */
export function buildDbtLabel(
  key: string,
  nestedObject: {
    dbt_project_name?: string | null;
    integration_name?: string | null;
    integration_environment_name?: string | null;
  }
): string {
  const projectName = nestedObject?.dbt_project_name;
  if (!projectName) return key;

  const parts: string[] = [];
  if (nestedObject.integration_name) parts.push(nestedObject.integration_name);
  if (nestedObject.integration_environment_name)
    parts.push(nestedObject.integration_environment_name);

  if (parts.length > 0) {
    return `${projectName} (${parts.join(" - ")})`;
  }

  // No metadata available — extract integration ID from the workflow_id key
  // Key format: dbt://integration_id.env_id/project_name
  try {
    const integrationId = key.split("://")[1].split(".")[0];
    return `${projectName} (#${integrationId})`;
  } catch {
    return projectName;
  }
}

interface UseDbtChartDataProps {
  chartType: ChartTypeEnum;
  graphData?: GetSnowflakeJobGraphData[];
  healthCheckData?: DbtHealthCheckResponse;
}

interface UseDbtChartDataReturn {
  chartData: Array<{ date?: string } & Record<string, number>>;
  labelMap: Record<string, string>;
  projectNames: string[];
}

export const useDbtChartData = ({
  chartType,
  graphData,
  healthCheckData,
}: UseDbtChartDataProps): UseDbtChartDataReturn => {
  const { chartData, labelMap } = useMemo(() => {
    if (chartType === ChartTypeEnum.COST) {
      if (!graphData) {
        return {
          chartData: [] as Array<{ date?: string } & Record<string, number>>,
          labelMap: {} as Record<string, string>,
        };
      }

      const result: Array<{ date?: string } & Record<string, number>> = [];
      const labels: Record<string, string> = {};
      for (const item of graphData) {
        const resultItem: { date?: string } & Record<string, number> = {};
        resultItem["date"] = item.date;

        for (const key in item) {
          if (key === "date") {
            continue;
          }

          const nestedObject = item[key];
          if (!nestedObject) {
            continue;
          }

          // Use the workflow_id key directly as the unique data key
          // to avoid collisions when multiple integrations share a project name
          labels[key] = buildDbtLabel(key, nestedObject);

          resultItem[key] = nestedObject.cost_value || 0;

          if (result.length > 0) {
            const lastItem = result[result.length - 1];
            if (lastItem[key] > 0) {
              resultItem[getPercentKey(key)] =
                (nestedObject.cost_value - lastItem[key]) / lastItem[key];
            }
          }
        }

        result.push(resultItem);
      }
      return { chartData: result, labelMap: labels };
    } else if (chartType === ChartTypeEnum.TEST_COVERAGE) {
      if (!healthCheckData?.results || healthCheckData.results.length === 0) {
        return {
          chartData: [] as Array<{ date?: string } & Record<string, number>>,
          labelMap: {} as Record<string, string>,
        };
      }

      const projectDataMap = new Map<string, Array<{ date: string; coverage: number }>>();

      healthCheckData.results.forEach(result => {
        const testCoverageInsight = result.project_insights.find(
          insight => insight.name === "Low test coverage in dbt models"
        );

        if (testCoverageInsight) {
          const projectName = result.dbt_project_name;
          const coverage = testCoverageInsight.insight_metadata?.coverage ?? 0;
          const formattedDate = dayjs(result.run_timestamp).format("MMM-DD");

          if (!projectDataMap.has(projectName)) {
            projectDataMap.set(projectName, []);
          }
          projectDataMap.get(projectName)!.push({ date: formattedDate, coverage });
        }
      });

      const dateMap = new Map<string, Record<string, number>>();

      projectDataMap.forEach((dataPoints, projectName) => {
        dataPoints.forEach(({ date, coverage }) => {
          if (!dateMap.has(date)) {
            dateMap.set(date, {});
          }
          dateMap.get(date)![projectName] = coverage;
        });
      });

      const chartDataArray = Array.from(dateMap.entries()).map(([date, projects]) => ({
        date,
        ...projects,
      })) as Array<{ date?: string } & Record<string, number>>;

      chartDataArray.sort((a, b) => dayjs(a.date, "MMM-DD").diff(dayjs(b.date, "MMM-DD")));

      return { chartData: chartDataArray, labelMap: {} as Record<string, string> };
    }

    return {
      chartData: [] as Array<{ date?: string } & Record<string, number>>,
      labelMap: {} as Record<string, string>,
    };
  }, [chartType, graphData, healthCheckData]);

  const projectNames = useMemo(() => {
    if (chartType !== ChartTypeEnum.TEST_COVERAGE || !chartData || chartData.length === 0) {
      return [];
    }
    const projects = new Set<string>();
    chartData.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== "date") {
          projects.add(key);
        }
      });
    });
    return Array.from(projects);
  }, [chartType, chartData]);

  return { chartData, labelMap, projectNames };
};

export const computeSelectedLegends = (
  graphData: GetSnowflakeJobGraphData[] | undefined
): { legends: string[]; labelMap: Record<string, string> } => {
  if (!graphData) return { legends: [], labelMap: {} };

  const totalCost: Record<string, number> = {};
  const labels: Record<string, string> = {};

  for (const item of graphData) {
    for (const key in item) {
      if (key === "date") continue;

      const nestedObject = item[key];
      if (!nestedObject) continue;

      // Use the workflow_id key directly for unique identification
      labels[key] = buildDbtLabel(key, nestedObject);

      totalCost[key] = totalCost[key] || 0;
      totalCost[key] += nestedObject?.cost_value || 0;
    }
  }

  const totalCostItems = Object.entries(totalCost);
  totalCostItems.sort((a, b) => b[1] - a[1]);

  const legends = (totalCostItems.length > 10 ? [OTHER] : []).concat(
    totalCostItems.slice(0, 10).map(x => x[0])
  );

  return { legends, labelMap: labels };
};
